// Collapsible SEO copy (<seo-text>). Two jobs, and the first matters
// more than the second: decide whether the toggle should exist at all,
// and open it at the right speed.
//
// The button starts hidden in the markup. It only appears once the text
// is measured taller than the cut, so a short paragraph never gets a
// "read more" with nothing behind it.
//
// The cut itself is CSS (--seo-collapsed), different per breakpoint,
// which is why the measurement runs again whenever the box resizes: the
// same text takes far more lines on a phone.

// Folga sobre a altura de corte. Sem ela, um texto que passa por duas
// linhas ganha um botão que revela quase nada.
const SLACK = 24;

class SeoText extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-seo-body]');
    this.toggle = this.querySelector('[data-seo-toggle]');
    if (!this.body || !this.toggle) return;

    this.listeners = new AbortController();
    const { signal } = this.listeners;

    this.toggle.addEventListener('click', this.handleToggle, { signal });

    // Reavalia quando a caixa muda de largura: girar o aparelho ou
    // atravessar o breakpoint muda a altura do mesmo texto.
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.body);

    // Fonte que chega tarde muda a altura depois do primeiro cálculo.
    if (document.fonts?.ready) document.fonts.ready.then(() => this.measure());

    this.measure();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.observer?.disconnect();
  }

  get cut() {
    return parseFloat(getComputedStyle(this).getPropertyValue('--seo-collapsed')) || 0;
  }

  // scrollHeight é a altura do conteúdo mesmo com a caixa cortada, que é
  // exatamente o que se precisa comparar contra o corte.
  measure() {
    const full = this.body.scrollHeight;
    const precisa = full > this.cut + SLACK;

    this.toggle.hidden = !precisa;
    // O atributo é o que liga o corte e o esmaecido no CSS. Sem ele, um
    // parágrafo curto ganhava um degradê por cima sem nada escondido
    // atrás, e o max-height limitava uma caixa que já cabia.
    this.toggleAttribute('truncated', precisa);

    // Texto que encolheu abaixo do corte não pode ficar preso aberto com
    // um botão que sumiu.
    if (!precisa && this.hasAttribute('open')) this.setOpen(false);

    // A altura de destino da animação, medida agora, para a transição ter
    // velocidade certa em texto curto e em texto longo.
    this.style.setProperty('--seo-open', `${full}px`);
  }

  handleToggle = () => this.setOpen(!this.hasAttribute('open'));

  setOpen(open) {
    this.toggleAttribute('open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.querySelector('[data-seo-more]').hidden = open;
    this.querySelector('[data-seo-less]').hidden = !open;

    // Fechar com o texto já rolado deixaria a pessoa no meio do nada,
    // olhando para o rodapé. Volta para o começo do bloco, e só quando
    // ele já saiu da tela por cima.
    if (!open && this.getBoundingClientRect().top < 0) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ block: 'start', behavior: reduce ? 'instant' : 'smooth' });
    }
  }
}

if (!customElements.get('seo-text')) {
  customElements.define('seo-text', SeoText);
}
